import React, { useCallback, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  styled,
  SvgIcon,
  Alert,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as MuiIcons from '@mui/icons-material';

import { ImpactAiPreset, PresetFormData, PresetAction, ContextStore } from '../types';
import { useAutoSetPrivileges } from '../hooks/useAutoSetPrivileges';
import { getPrivilegeMappingDetails } from '../utils/inferPrivilegesFromActions';

import IconSelector from './IconSelector';
import ActionsEditor from './ActionsEditor/index';
import InitialContextEditor from './InitialContextEditor';
import RelatedPresetsSelector from './RelatedPresetsSelector';
import PrivilegeSelector, { PRIVILEGE_DESCRIPTIONS } from './PrivilegeSelector';

const PreviewCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
}));

const PreviewIconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const PreviewIcon = styled(SvgIcon)(({ theme }) => ({
  fontSize: 24,
  color: theme.palette.text.primary,
}));

const DynamicIcon = styled(SvgIcon, {
  shouldForwardProp: (prop) => prop !== 'iconColor',
})<{ iconColor: string }>(({ iconColor }) => ({
  fontSize: 24,
  color: iconColor,
}));

interface PresetEditorProps {
  preset: Partial<ImpactAiPreset>;
  isEditing: boolean;
  formData: PresetFormData;
  onFormDataChange: (field: keyof PresetFormData, value: unknown) => void;
  onPresetChange: (preset: Partial<ImpactAiPreset>) => void;
  availablePresets: ImpactAiPreset[];
}

const PresetEditor: React.FC<PresetEditorProps> = ({
  preset,
  isEditing,
  formData,
  onFormDataChange,
  onPresetChange,
  availablePresets,
}) => {
  // Accordion states
  const [accessControlExpanded, setAccessControlExpanded] = useState(false);
  const [relatedPresetsExpanded, setRelatedPresetsExpanded] = useState(false);
  const [initialContextExpanded, setInitialContextExpanded] = useState(false);

  // Handle actions change
  const handleActionsChange = useCallback(
    (actions: PresetAction[]) => {
      onPresetChange({
        ...preset,
        actions,
      });
    },
    [preset, onPresetChange]
  );

  // Handle initial context change
  const handleInitialContextChange = useCallback(
    (initialContext: ContextStore) => {
      onPresetChange({
        ...preset,
        initialContext,
      });
    },
    [preset, onPresetChange]
  );

  // Handle form field changes that should update the preset
  const handleFormFieldChange = useCallback(
    (field: keyof PresetFormData, value: unknown) => {
      onFormDataChange(field, value);
      onPresetChange({
        ...preset,
        [field]: value,
      });
    },
    [preset, onFormDataChange, onPresetChange]
  );

  // Auto-set privileges based on navigate actions
  const { inferredPrivileges, hasManualEdits } = useAutoSetPrivileges({
    preset,
    formData,
    onPrivilegesChange: (privileges) => {
      handleFormFieldChange('requiredPrivileges', privileges);
    },
    enabled: isEditing,
  });

  // Get detailed mapping of URLs to privileges
  const privilegeMappings = getPrivilegeMappingDetails(preset.actions || []);

  return (
    <Box flex={1} p={2} overflow="auto">
      {isEditing ? (
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Basic Configuration */}
          <PreviewCard elevation={0}>
            <Typography variant="h6" gutterBottom>
              Basic Configuration
            </Typography>
            <Box display="flex" gap={3} alignItems="flex-start">
              {/* Left side - Form fields */}
              <Box flex={1} display="grid" gridTemplateColumns="1fr 1fr" gap={2} alignItems="start">
                {/* Left column - Title and Description */}
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    label="Title"
                    value={formData.title}
                    onChange={(e) => handleFormFieldChange('title', e.target.value)}
                    required
                    variant="outlined"
                    size="small"
                    fullWidth
                    helperText="Supports HTML tags like <strong> and <em>"
                  />
                  <TextField
                    label="Description"
                    value={formData.description}
                    onChange={(e) => handleFormFieldChange('description', e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    helperText="Brief description shown in the preset card"
                  />
                </Box>

                {/* Right column - Icon selector and controls */}
                <Box display="flex" flexDirection="column" gap={2}>
                  <IconSelector
                    value={formData.iconName}
                    onChange={(iconName) => handleFormFieldChange('iconName', iconName)}
                    label="Icon"
                  />
                  <Box display="flex" gap={2} alignItems="center">
                    <TextField
                      label="Color"
                      value={formData.color}
                      onChange={(e) => handleFormFieldChange('color', e.target.value)}
                      type="color"
                      variant="outlined"
                      size="small"
                      sx={{ width: 100 }}
                      InputProps={{
                        sx: { height: 40 },
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.enabled}
                          onChange={(e) => handleFormFieldChange('enabled', e.target.checked)}
                          size="small"
                        />
                      }
                      label="Visible"
                    />
                    <TextField
                      label="Sort"
                      value={formData.sortIndex ?? ''}
                      onChange={(e) =>
                        handleFormFieldChange(
                          'sortIndex',
                          e.target.value ? Number(e.target.value) : undefined
                        )
                      }
                      type="number"
                      variant="outlined"
                      size="small"
                      sx={{ width: 80 }}
                      InputProps={{
                        sx: { height: 40 },
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Right side - Preview */}
              <Box
                sx={{
                  width: 320,
                  p: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'grey.50',
                }}
              >
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  Preview
                </Typography>
                <PreviewIconContainer>
                  {(() => {
                    const IconComponent = (
                      MuiIcons as Record<string, React.ComponentType<unknown>>
                    )[formData.iconName];

                    if (IconComponent) {
                      return <DynamicIcon as={IconComponent} iconColor={formData.color} />;
                    }

                    return <PreviewIcon sx={{ color: formData.color }}>?</PreviewIcon>;
                  })()}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={500}
                      dangerouslySetInnerHTML={{ __html: formData.title || 'Untitled Preset' }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      dangerouslySetInnerHTML={{
                        __html: formData.description || 'No description',
                      }}
                    />
                  </Box>
                </PreviewIconContainer>
                <Typography variant="caption" color="text.secondary">
                  How it appears in ImpactAI drawer
                </Typography>
              </Box>
            </Box>
          </PreviewCard>

          {/* Grouped Accordions */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            {/* Access Control Configuration - Accordion */}
            <Accordion
              expanded={accessControlExpanded}
              onChange={(_, expanded) => setAccessControlExpanded(expanded)}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'grey.50',
                  borderRadius: accessControlExpanded ? '4px 4px 0 0' : 1,
                }}
              >
                <Typography variant="h6">
                  Access Control
                  {formData.requiredPrivileges && formData.requiredPrivileges.length > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 2 }}
                    >
                      {formData.requiredPrivileges.length} privilege
                      {formData.requiredPrivileges.length !== 1 ? 's' : ''} configured
                    </Typography>
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3 }}>
                <Box>
                  <PrivilegeSelector
                    value={formData.requiredPrivileges || []}
                    onChange={(value) => handleFormFieldChange('requiredPrivileges', value)}
                    helperText={
                      inferredPrivileges.length > 0
                        ? `Auto-detected privileges based on navigate actions. ${
                            hasManualEdits ? 'Manual changes preserved.' : ''
                          }`
                        : 'Select privileges required to access this preset.'
                    }
                  />
                  {privilegeMappings.length > 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="caption" component="div">
                        <strong>Auto-detected from navigate URLs:</strong>
                        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                          {privilegeMappings.map(({ url, privilege }, index) => (
                            <li key={index}>
                              <code>{url}</code> →{' '}
                              {PRIVILEGE_DESCRIPTIONS[privilege]?.split(' - ')[0] || privilege}
                            </li>
                          ))}
                        </Box>
                      </Typography>
                    </Alert>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Initial Context Configuration - Accordion */}
            <Accordion
              expanded={initialContextExpanded}
              onChange={(_, expanded) => setInitialContextExpanded(expanded)}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'grey.50',
                  borderRadius: initialContextExpanded ? '4px 4px 0 0' : 1,
                }}
              >
                <Typography variant="h6">
                  Initial Context
                  {preset.initialContext && Object.keys(preset.initialContext).length > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 2 }}
                    >
                      {Object.keys(preset.initialContext).length} variable
                      {Object.keys(preset.initialContext).length !== 1 ? 's' : ''} defined
                    </Typography>
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3 }}>
                <InitialContextEditor
                  initialContext={preset.initialContext || {}}
                  onChange={handleInitialContextChange}
                />
              </AccordionDetails>
            </Accordion>

            {/* Related Presets Configuration - Accordion */}
            <Accordion
              expanded={relatedPresetsExpanded}
              onChange={(_, expanded) => setRelatedPresetsExpanded(expanded)}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'grey.50',
                  borderRadius: relatedPresetsExpanded ? '4px 4px 0 0' : 1,
                }}
              >
                <Typography variant="h6">
                  Related Presets
                  {formData.relatedPresets && formData.relatedPresets.length > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 2 }}
                    >
                      {formData.relatedPresets.length} preset
                      {formData.relatedPresets.length !== 1 ? 's' : ''} linked
                    </Typography>
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3 }}>
                <RelatedPresetsSelector
                  value={formData.relatedPresets || []}
                  onChange={(value) => handleFormFieldChange('relatedPresets', value)}
                  availablePresets={availablePresets}
                  currentPresetId={preset.id}
                />
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Actions Configuration */}
          <Box>
            <ActionsEditor
              preset={preset}
              onChange={handleActionsChange}
              availablePresets={availablePresets}
            />
          </Box>

          {/* Help Information */}
          <Alert severity="info">
            <Typography variant="body2" gutterBottom>
              <strong>Tips for creating effective presets:</strong>
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
              <li>Use template inputs to collect structured information early in the flow</li>
              <li>Set context values that can be reused throughout the conversation</li>
              <li>Add delays to bot messages for a more natural conversation feel</li>
              <li>Use the navigate action to guide users to the appropriate forms</li>
              <li>Test your preset thoroughly before enabling it</li>
            </Typography>
          </Alert>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Basic Configuration - Read Only */}
          <PreviewCard elevation={0}>
            <Typography variant="h6" gutterBottom>
              Basic Configuration
            </Typography>
            <Box display="flex" gap={3} alignItems="flex-start">
              {/* Left side - Form fields (read-only) */}
              <Box flex={1} display="grid" gridTemplateColumns="1fr 1fr" gap={2} alignItems="start">
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    label="Title"
                    value={formData.title}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Description"
                    value={formData.description}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    InputProps={{ readOnly: true }}
                  />
                </Box>

                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    label="Icon"
                    value={formData.iconName}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <Box display="flex" gap={2} alignItems="center">
                    <TextField
                      label="Color"
                      value={formData.color}
                      type="color"
                      variant="outlined"
                      size="small"
                      sx={{ width: 100 }}
                      InputProps={{
                        readOnly: true,
                        sx: { height: 40 },
                      }}
                    />
                    <FormControlLabel
                      control={<Switch checked={formData.enabled} size="small" disabled />}
                      label="Enabled"
                    />
                    <TextField
                      label="Sort"
                      value={formData.sortIndex ?? ''}
                      variant="outlined"
                      size="small"
                      sx={{ width: 80 }}
                      InputProps={{
                        readOnly: true,
                        sx: { height: 40 },
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Right side - Preview */}
              <Box
                sx={{
                  width: 320,
                  p: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'grey.50',
                }}
              >
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                  Preview
                </Typography>
                <PreviewIconContainer>
                  {(() => {
                    const IconComponent = (
                      MuiIcons as Record<string, React.ComponentType<unknown>>
                    )[formData.iconName];

                    if (IconComponent) {
                      return <DynamicIcon as={IconComponent} iconColor={formData.color} />;
                    }

                    return <PreviewIcon sx={{ color: formData.color }}>?</PreviewIcon>;
                  })()}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={500}
                      dangerouslySetInnerHTML={{ __html: formData.title || 'Untitled Preset' }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      dangerouslySetInnerHTML={{
                        __html: formData.description || 'No description',
                      }}
                    />
                  </Box>
                </PreviewIconContainer>
                <Typography variant="caption" color="text.secondary">
                  How it appears in ImpactAI drawer
                </Typography>
              </Box>
            </Box>
          </PreviewCard>

          {/* Grouped Accordions - Read Only */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            {/* Access Control Configuration - Read Only - Accordion */}
            <Accordion
              expanded={accessControlExpanded}
              onChange={(_, expanded) => setAccessControlExpanded(expanded)}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'grey.50',
                  borderRadius: accessControlExpanded ? '4px 4px 0 0' : 1,
                }}
              >
                <Typography variant="h6">
                  Access Control
                  {formData.requiredPrivileges && formData.requiredPrivileges.length > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 2 }}
                    >
                      {formData.requiredPrivileges.length} privilege
                      {formData.requiredPrivileges.length !== 1 ? 's' : ''} configured
                    </Typography>
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3 }}>
                <PrivilegeSelector value={formData.requiredPrivileges || []} onChange={() => {}} />
              </AccordionDetails>
            </Accordion>

            {/* Initial Context Configuration - Read Only - Accordion */}
            <Accordion
              expanded={initialContextExpanded}
              onChange={(_, expanded) => setInitialContextExpanded(expanded)}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'grey.50',
                  borderRadius: initialContextExpanded ? '4px 4px 0 0' : 1,
                }}
              >
                <Typography variant="h6">
                  Initial Context
                  {preset.initialContext && Object.keys(preset.initialContext).length > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 2 }}
                    >
                      {Object.keys(preset.initialContext).length} variable
                      {Object.keys(preset.initialContext).length !== 1 ? 's' : ''} defined
                    </Typography>
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3 }}>
                <InitialContextEditor
                  initialContext={preset.initialContext || {}}
                  onChange={() => {}}
                />
              </AccordionDetails>
            </Accordion>

            {/* Related Presets Configuration - Read Only - Accordion */}
            <Accordion
              expanded={relatedPresetsExpanded}
              onChange={(_, expanded) => setRelatedPresetsExpanded(expanded)}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'grey.50',
                  borderRadius: relatedPresetsExpanded ? '4px 4px 0 0' : 1,
                }}
              >
                <Typography variant="h6">
                  Related Presets
                  {formData.relatedPresets && formData.relatedPresets.length > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 2 }}
                    >
                      {formData.relatedPresets.length} preset
                      {formData.relatedPresets.length !== 1 ? 's' : ''} linked
                    </Typography>
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3 }}>
                <RelatedPresetsSelector
                  value={formData.relatedPresets || []}
                  onChange={() => {}}
                  availablePresets={availablePresets}
                  currentPresetId={preset.id}
                />
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Actions Configuration - Read Only */}
          <Box>
            <ActionsEditor
              preset={preset}
              onChange={() => {}}
              availablePresets={availablePresets}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(PresetEditor);
